import { useState } from 'react';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { DEBRIS_TYPES } from '../../../types/debris';
import type { DebrisZone, DebrisType } from '../../../types/debris';
import { notifications } from '../../../lib/notifications';
import { AddressAutocomplete } from '../../../components/ui/AddressAutocomplete';
import { IconCheck, IconCurrentLocation } from '@tabler/icons-react';

interface Props { zone: DebrisZone; }

export default function DebrisPortalAssessment({ zone }: Props) {
  const { user } = useAuthStore();
  const { createLoadTicket } = useDebrisOpsStore();

  const [address, setAddress] = useState('');
  const [addrLat, setAddrLat] = useState(0);
  const [addrLng, setAddrLng] = useState(0);
  const [debrisType, setDebrisType] = useState<DebrisType | ''>('');
  const [estimatedCY, setEstimatedCY] = useState('');
  const [rightOfEntry, setRightOfEntry] = useState<'yes' | 'no' | ''>('');
  const [hazards, setHazards] = useState('');
  const [notes, setNotes] = useState('');
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const captureGPS = () => {
    setLocating(true);
    navigator.geolocation?.getCurrentPosition(
      pos => {
        setAddrLat(pos.coords.latitude);
        setAddrLng(pos.coords.longitude);
        setAddress(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setLocating(false);
      },
      () => {
        notifications.show({ message: 'GPS unavailable — enter address manually', color: '#F86A0B' });
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleSubmit = async () => {
    if (!address.trim()) { notifications.show({ message: 'Enter location or capture GPS', color: 'var(--color-error)' }); return; }
    if (!debrisType) { notifications.show({ message: 'Select debris type', color: 'var(--color-error)' }); return; }

    setSaving(true);
    try {
      await createLoadTicket({
        zoneId: String(zone.id),
        debrisType,
        measurementMode: DEBRIS_TYPES[debrisType]?.measurementMode ?? 'volumetric',
        calculatedCubicYards: estimatedCY || '0',
        pickupAddress: address.trim(),
        pickupLat: addrLat ? String(addrLat) : '',
        pickupLng: addrLng ? String(addrLng) : '',
        pickupTime: new Date().toISOString(),
        status: 'draft',
        notes: [
          rightOfEntry ? `ROE collected: ${rightOfEntry}` : '',
          hazards ? `Hazards: ${hazards}` : '',
          notes,
        ].filter(Boolean).join(' | '),
        loadingMonitorName: (user as any)?.email ?? '',
      } as any);

      setDone(true);
      setTimeout(() => {
        setDone(false);
        setAddress('');
        setDebrisType('');
        setEstimatedCY('');
        setRightOfEntry('');
        setHazards('');
        setNotes('');
      }, 2000);
    } catch {
      notifications.show({ message: 'Failed to save assessment', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#dcfce7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <IconCheck size={32} color="#16a34a" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginBottom: 8 }}>Assessment Saved</div>
        <div style={{ fontSize: 14, color: '#7B7D7F' }}>Field assessment logged for zone {zone.zoneName || zone.zoneCode}.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Zone badge */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
        background: '#fef3c7', borderRadius: 4, padding: '8px 16px',
      }}>
        <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>Field Assessment · {zone.zoneName || zone.zoneCode}</span>
      </div>

      {/* Location */}
      <div className="sp-card">
        <div className="sp-card-title">Location</div>
        <div className="sp-field">
          <AddressAutocomplete
            value={address}
            onChange={(val, lat, lng) => { setAddress(val); if (lat) setAddrLat(lat); if (lng) setAddrLng(lng); }}
            label="Address / Coordinates"
            required
            placeholder="Street address or cross streets"
          />
        </div>
        <button
          className="sp-btn sp-btn-secondary"
          style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
          onClick={captureGPS} disabled={locating}
        >
          <IconCurrentLocation size={16} />
          {locating ? 'Getting GPS…' : 'Use My Location'}
        </button>
      </div>

      {/* Debris Type */}
      <div className="sp-card">
        <div className="sp-card-title">Debris Type</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {(Object.keys(DEBRIS_TYPES) as DebrisType[]).map(type => {
            const cfg = DEBRIS_TYPES[type];
            const active = debrisType === type;
            return (
              <button key={type} onClick={() => setDebrisType(type)} style={{
                padding: '9px 8px', border: '1.5px solid', borderRadius: 4,
                fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer',
                fontFamily: 'inherit', textAlign: 'left',
                background: active ? `${cfg.color}18` : 'white',
                color: active ? cfg.color : '#475569',
                borderColor: active ? cfg.color : '#e2e8f0',
              }}>
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Estimated quantity */}
      <div className="sp-card">
        <div className="sp-card-title">Estimated Quantity</div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Estimated CY (if known)</label>
          <input className="sp-input" type="number" min={0} value={estimatedCY}
            onChange={e => setEstimatedCY(e.target.value)} placeholder="e.g. 50" />
        </div>
      </div>

      {/* Right of Entry */}
      <div className="sp-card">
        <div className="sp-card-title">Right of Entry</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['yes', 'no'] as const).map(v => (
            <button key={v} onClick={() => setRightOfEntry(v)} style={{
              flex: 1, padding: '8px 0', border: '1.5px solid', borderRadius: 4,
              fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              textTransform: 'capitalize',
              background: rightOfEntry === v ? (v === 'yes' ? '#059669' : '#B12525') : 'white',
              color: rightOfEntry === v ? 'white' : '#475569',
              borderColor: rightOfEntry === v ? (v === 'yes' ? '#059669' : '#B12525') : '#e2e8f0',
            }}>
              {v === 'yes' ? '✓ ROE Collected' : '✗ No ROE'}
            </button>
          ))}
        </div>
      </div>

      {/* Hazards */}
      <div className="sp-card">
        <div className="sp-card-title">Hazards / Special Conditions</div>
        <div className="sp-field">
          <label className="sp-label">Hazards</label>
          <input className="sp-input" value={hazards} onChange={e => setHazards(e.target.value)}
            placeholder="Power lines, gas leak, unstable structure…" />
        </div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Additional Notes</label>
          <textarea className="sp-textarea" style={{ minHeight: 64 }} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Description of debris, access conditions…" />
        </div>
      </div>

      <button className="sp-btn sp-btn-primary" onClick={handleSubmit} disabled={saving} style={{ marginTop: 4 }}>
        {saving ? 'Saving…' : 'Save Field Assessment'}
      </button>
    </div>
  );
}
